// Pure helpers for the Mode A order-creation flow (inline header +
// CreateOrderScreen). Extracted from ServiceLevelOrderModal so the modal can be
// retired. No React, no I/O — unit-testable with node.

// Build { [catalogServiceLevelId]: { order_label, move_type_code, short_code,
// display_color, alias_name } } from the `catalog` endpoint response.
export const buildServiceLevelIndex = (catalogData) => {
  const index = {};
  (catalogData || []).forEach((omt) => {
    (omt.service_levels || []).forEach((sl) => {
      index[sl.id] = {
        order_label: omt.move_type?.order_label,
        move_type_code: omt.move_type?.code,
        // base_order_type (CustomerOrder.type_of_order) from the catalog, so Mode A
        // can derive the order type for DB-only move types without an
        // enhancedOrderTypes entry (P5/P3 composition).
        base_order_type: omt.move_type?.base_order_type,
        short_code: omt.short_code,
        display_color: omt.display_color,
        alias_name: omt.alias_name,
      };
    });
  });
  return index;
};

// Per-leg los_code array from a `forAccount` service level's effective legs, in
// the shape CreateOrderScreen consumes.
export const buildLegLos = (sl) =>
  (sl?.leg_los || []).map((l) => ({
    leg_sequence: l.leg_sequence,
    location_role: l.location_role,
    los_code: l.effective_los_code,
    // Task 5 (combo SL UI polish): per-leg combo flag from the `forAccount`/
    // catalog serialize_effective payload (Task 3, merged). Non-combo SLs and
    // legs simply carry combo: false/undefined; consumers (CreateOrderScreen /
    // AddressLocationDetails) use it to lock the tariff input on every leg
    // except the combo leg when the selected SL is combo.
    combo: !!l.combo,
  }));

export const findApplicableServiceLevel = (serviceLevels, serviceLevelId) => {
  if (serviceLevelId == null || serviceLevelId === "") return null;
  const target = String(serviceLevelId);
  return (Array.isArray(serviceLevels) ? serviceLevels : []).find(
    (sl) => String(sl?.service_level_id ?? sl?.id ?? "") === target
  ) || null;
};

export const resolveOrderServiceLevelLegLos = (serviceLevels, serviceLevelId) => {
  const sl = findApplicableServiceLevel(serviceLevels, serviceLevelId);
  return sl ? buildLegLos(sl) : null;
};

// Phase 4.1 Mode A: maps a leg_los entry's location_role (from the service-level
// picker) onto the location's type_of_loc used across the order screens. LINEHAUL
// legs have no corresponding stop on the order and are intentionally omitted, so
// they are skipped rather than mis-mapped.
export const LEG_LOS_LOCATION_ROLE_TO_TYPE_OF_LOC = {
  SHIPPER_PICKUP: "PICKUP",
  INBOUND_DOCK: "PICKUP",
  INTERLINE_RECOVERY: "PICKUP",
  CONSIGNEE_DELIVERY: "DELIVERY",
  OUTBOUND_DOCK: "DELIVERY",
  INTERLINE_TRANSFER: "DELIVERY",
};

// Phase 4.1 Mode A: resolve the effective los_code (from a service level's
// leg_los) for a single order location, or undefined when there's no
// unambiguous, selectable match. Shared by CreateOrderScreen (prefill/seed) and
// the OLOS per-leg lock on both the create/edit form and the View Order screen.
// Returns undefined (never throws) for: no matching leg_los entry, LINEHAUL-only
// legs (no location role in the map), ambiguous multi-leg same-type matches that
// sequence doesn't disambiguate, or a los_code not present in the currently
// loaded levelOfServices.
// Resolve the single leg_los ENTRY that maps to a given order Location (same
// location_role→type_of_loc + sequence disambiguation resolveLegLosCode uses).
// Returns the matched entry object (carrying los_code AND the Task 5 `combo`
// flag) or undefined for no/ambiguous match. Shared so the LOS-code resolve and
// the combo-leg lock stay consistent on the exact same leg→Location matching.
export const resolveLegLosEntry = (loc, legLos) => {
  if (!loc || !loc.type_of_loc) return undefined;
  if (!Array.isArray(legLos) || legLos.length === 0) return undefined;
  const candidates = legLos.filter(entry => LEG_LOS_LOCATION_ROLE_TO_TYPE_OF_LOC[entry?.location_role] === loc.type_of_loc);
  if (candidates.length === 0) return undefined;
  // Multiple legs share this type_of_loc (e.g. multi-stop): disambiguate by
  // sequence. If it doesn't resolve unambiguously, bail rather than guessing.
  return candidates.length === 1
    ? candidates[0]
    : candidates.find(entry => entry.leg_sequence === loc.loc_order_sequence);
};

export const resolveLegLosCode = (loc, legLos, levelOfServices) => {
  const matchedEntry = resolveLegLosEntry(loc, legLos);
  const losCode = matchedEntry?.los_code;
  if (!losCode) return undefined;
  // Only honor the leg's los_code if it's actually a selectable option for this
  // account/order-type; otherwise leave it unset so the existing default-LOS
  // fallback applies as normal.
  const isSelectableLos = (levelOfServices || []).some(los => los.code === losCode);
  return isSelectableLos ? losCode : undefined;
};

// Union a service level's own per-leg default LOS into a base option list (the
// account-scoped levelOfServices), so an Account-Defaults SL default sourced from
// the org Standard rate sheet is selectable + prefillable even when the account's
// own rate sheet doesn't carry it. Adds ONLY the SL's own codes (not the whole
// Standard catalog); "NONE" is skipped.
//
// metaByCode: optional { code -> { id, name } } — the org Standard rate sheet's
// LOS id + display name. The unioned option carries the Standard LOS's real `id`,
// so it is a DISTINCT, selectable record even when the account already has the
// same code (both appear — dedup is by `id`, not `code`). That's what lets the
// leg dropdown show BOTH the account's "TT" and the Standard "SMF TRACTOR TRAILER
// (TT)" so an operator can pick the specific one. Falls back to the bare code for
// id/name when the Standard meta isn't loaded.
// Group labels for the leg LOS dropdown (antd OptGroup). Only applied when a
// Standard-sheet default is actually unioned in, so a plain account-only list
// stays ungrouped (no header noise).
export const LOS_GROUP_ACCOUNT = "Account Tariffs";
export const LOS_GROUP_STANDARD = "Standard Rate Sheet";

export const withServiceLevelDefaultCodes = (options, legLos, metaByCode = {}) => {
  const base = options || [];
  if (!Array.isArray(legLos) || legLos.length === 0) return base;
  const presentIds = new Set(base.map((o) => o.id).filter(Boolean));
  const extra = [];
  legLos.forEach((l) => {
    const code = l && l.los_code;
    if (!code || code === "NONE") return;
    const meta = metaByCode[code] || {};
    const id = meta.id || code; // synthetic fallback id when the Standard meta is absent
    if (presentIds.has(id)) return; // exact same LOS record already in the list
    presentIds.add(id);
    // rate_sheet_id is the resolution key sent to the backend (los_code + rate_sheet_id).
    // The Standard default carries the org default sheet's id; account options have none.
    extra.push({ id, code, name: meta.name || code, rate_sheet_id: meta.rate_sheet_id || "", los_group: LOS_GROUP_STANDARD });
  });
  if (!extra.length) return base;
  // Tag account options so the dropdown can group them separately from the
  // Standard-sheet defaults — only when there IS a Standard extra to separate.
  return [...base.map((o) => (o.los_group ? o : { ...o, los_group: LOS_GROUP_ACCOUNT })), ...extra];
};

// Union the FULL org Standard rate-sheet LOS list into the account option list,
// grouped by source ("Account Tariffs" / "Standard Rate Sheet"). Per product: the
// leg dropdown shows every Standard tariff, not only the SL-mapped defaults.
// standardLosList: [{ id, code, name, rate_sheet_id }]. Dedup by (code + rate_sheet_id)
// so an account LOS and a same-code Standard LOS both appear. ALSO dedup by `id`:
// an account linked to the Standard sheet gets its LOS FROM that sheet, so the two
// lists are the SAME LevelOfService records (same ids). Without the id check they'd
// double-list ("two sets") and antd would highlight both same-id options on open.
// "NONE" is skipped (the account list already carries its own NONE). Returns base
// unchanged when there is no Standard list (keeps account-only lists ungrouped).
export const withAllStandardLos = (options, standardLosList) => {
  const base = options || [];
  const list = Array.isArray(standardLosList) ? standardLosList : [];
  if (!list.length) return base;
  const keyOf = (o) => `${o.code}::${o.rate_sheet_id || ""}`;
  const present = new Set(base.map(keyOf));
  const presentIds = new Set(base.map((o) => o.id).filter(Boolean).map(String));
  const extra = [];
  list.forEach((o) => {
    if (!o || !o.code || o.code === "NONE") return;
    const opt = { id: o.id || o.code, code: o.code, name: o.name || o.code, rate_sheet_id: o.rate_sheet_id || "", los_group: LOS_GROUP_STANDARD };
    // Same record already in the account list (linked-to-standard account) OR same
    // code+sheet already present → skip, so each tariff appears exactly once.
    if (presentIds.has(String(opt.id)) || present.has(keyOf(opt))) return;
    present.add(keyOf(opt));
    presentIds.add(String(opt.id));
    extra.push(opt);
  });
  if (!extra.length) return base;
  return [...base.map((o) => (o.los_group ? o : { ...o, los_group: LOS_GROUP_ACCOUNT })), ...extra];
};

// Union the FULL account rate-sheet LOS into the base account option list, grouped
// as "Account Tariffs". The base (account_los_short_list) is restricted to the
// account's single per-order-type designated LOS (OrderTypeLevelOfService.los_codes)
// — but per product the Mode A leg dropdown should offer EVERY tariff on the
// account's own rate sheet, symmetric with withAllStandardLos showing the whole
// Standard sheet. Without this, an account-ratesheet LOS that isn't the designated
// code (e.g. a same-code twin of a Standard tariff) is only reachable as a Standard
// option and bills against Standard rates. accountLosList: [{ id, code, name,
// rate_sheet_id }] from the los_options single-account endpoint (los_scope_query —
// the account's linked rate sheet, or its account-scoped LOS when unlinked).
//
// Dedup within the account group is by CODE (one rate sheet ⇒ same code = same
// tariff): the designated option already in base (blank rate_sheet_id) suppresses
// its accountLosList twin, so it isn't double-listed. Cross-group same-code twins
// (account vs Standard) are intentionally KEPT — withAllStandardLos dedups only by
// id and code+rate_sheet_id, so the Standard-sheet version still appears in its own
// group (operator picks account rate vs Standard rate). "NONE" is skipped.
export const withAllAccountLos = (options, accountLosList) => {
  const base = options || [];
  const list = Array.isArray(accountLosList) ? accountLosList : [];
  if (!list.length) return base;
  const presentIds = new Set(base.map((o) => o.id).filter(Boolean).map(String));
  const presentCodes = new Set(base.map((o) => o.code));
  const extra = [];
  list.forEach((o) => {
    if (!o || !o.code || o.code === "NONE") return;
    // Already present by id, or the same account tariff code is already in base
    // (the OTLOS-designated option, which carries a blank rate_sheet_id) → skip.
    if (presentIds.has(String(o.id)) || presentCodes.has(o.code)) return;
    presentIds.add(String(o.id));
    presentCodes.add(o.code);
    extra.push({ id: o.id || o.code, code: o.code, name: o.name || o.code, rate_sheet_id: o.rate_sheet_id || "", los_group: LOS_GROUP_ACCOUNT });
  });
  if (!extra.length) return base;
  return [...base.map((o) => (o.los_group ? o : { ...o, los_group: LOS_GROUP_ACCOUNT })), ...extra];
};

// Resolve the move-type order_label for a service level. null when the mapping
// is missing — caller must fail loudly, never invent a code.
export const labelCodeForServiceLevel = (serviceLevelId, index) =>
  (index && index[serviceLevelId] && index[serviceLevelId].order_label) || null;

// ── DB-driven order types (P3) ────────────────────────────────────────────────

// Labels whose DB leg representation does not yet reproduce the legacy form
// config byte-for-byte, so they keep resolving via enhancedOrderTypes even when
// the db_order_types flag is on:
// - CDK: legacy form renders the DRPF (drop-off) leg FIRST (loc_order_sequence 0)
//   while the DB's structural order is inbound-first — swapping would change
//   stored Location.loc_order_sequence for cross-dock orders.
// - WO: legacy form has TWO locations (DRPF + R) but the move type has one
//   structural leg (inbound dock).
// Resolving these needs a product call (e.g. a form_sequence on MoveTypeLeg) —
// until then they are explicitly legacy-parity fallbacks.
export const DB_ORDER_TYPE_LEGACY_FALLBACK = ["CDK", "WO"];

// Build { [order_label]: enhancedOrderTypes-shaped entry } from the `catalog`
// endpoint response, so getOrderTypeConfig can resolve order-type config from
// the DB instead of the hardcoded frontend array. LINEHAUL legs are transport
// segments and create no Location row, so they are excluded from locations[];
// loc_order_sequence is the index among the remaining (CS) legs, matching the
// legacy 0/1 convention.
export const buildOrderTypeConfigIndex = (catalogData) => {
  const index = {};
  (catalogData || []).forEach((omt) => {
    const mt = omt.move_type || {};
    if (!mt.order_label || !mt.base_order_type) return;
    const legs = (mt.legs || []).filter((l) => l.type_of_loc !== "LINEHAUL");
    if (legs.length === 0) return;
    index[mt.order_label] = {
      code: mt.order_label,
      type_of_order: mt.base_order_type,
      label: omt.alias_name,
      description: omt.description,
      toolTipLabel: omt.tooltip_label,
      message: omt.tooltip_message || [],
      // Badge fields (OMT short_code + display_color): let screens render the
      // catalog MoveTypeBadge for a DB-resolved order type. Legacy
      // enhancedOrderTypes entries never carry these, so their absence is the
      // "legacy config" signal and all legacy rendering is untouched.
      short_code: omt.short_code,
      display_color: omt.display_color,
      locations: legs.map((l, i) => ({
        type_of_loc: l.type_of_loc,
        label_code: l.label_code,
        loc_order_sequence: i,
        // DB-driven display name for the leg heading ("Shipper Pickup"). Only
        // present on catalog-built configs — legacy enhancedOrderTypes locations
        // never carry it, so the form's label_code-switch fallback still runs
        // byte-identically when service_level_flow_enabled is off.
        leg_name: l.leg_name,
      })),
    };
  });
  return index;
};

// Normalize account LOS rows (or pre-merged losOptions) into a consistent shape for
// Select_v2 dropdowns.
export const normalizeLosOptionsList = (levelOfServices = []) => (
  (Array.isArray(levelOfServices) ? levelOfServices : [])
    .map((item, idx) => {
      if (!item) return null;
      if (typeof item === "string" || typeof item === "number") {
        const s = String(item).trim();
        return s ? { id: s, code: s, name: s } : null;
      }
      const code = typeof item.code === "string" ? item.code : (item.id ? String(item.id) : "");
      const name = typeof item.name === "string" ? item.name : (code || `LOS ${idx}`);
      const id = item.id || item._id || code || `los-${idx}`;
      return {
        id: String(id),
        code: String(code || name),
        name: String(name),
        rate_sheet_id: item.rate_sheet_id ? String(item.rate_sheet_id) : "",
        los_group: item.los_group || "",
        default_pickup_los: item.default_pickup_los,
        default_delivery_los: item.default_delivery_los,
      };
    })
    .filter(Boolean)
);

// Same id-vs-code resolution as AddressLocationDetails' leg LOS dropdown.
export const resolveLosDropdownValue = (losOptions = [], { los_code, los_id } = {}) => {
  if (!los_code && !los_id) return undefined;
  const idMatchesCode = los_id && losOptions.some(
    (o) => String(o.id) === String(los_id) && o.code === los_code
  );
  if (idMatchesCode) return String(los_id);
  const byCode = losOptions.find((o) => o.code === los_code);
  return byCode?.id || los_code || undefined;
};

export const findLosOption = (losOptions = [], { los_code, los_id, rate_sheet_id } = {}) => {
  if (los_id) {
    const losIdStr = String(los_id);
    const byId = losOptions.find(
      (o) => String(o.id) === losIdStr || String(o.los_id || "") === losIdStr
    );
    if (byId) return byId;
  }
  if (los_code && rate_sheet_id) {
    const byCodeAndSheet = losOptions.find(
      (o) => o.code === los_code && String(o.rate_sheet_id || "") === String(rate_sheet_id)
    );
    if (byCodeAndSheet) return byCodeAndSheet;
  }
  if (los_code) {
    return losOptions.find((o) => o.code === los_code) || null;
  }
  return null;
};

export const resolveLinehaulTariffFromTracking = (tracking = {}, losOptions = []) => {
  const tariff = tracking?.tariff || tracking;
  return findLosOption(losOptions, {
    los_id: tariff?.los_id,
    los_code: tariff?.los_code,
    rate_sheet_id: tariff?.rate_sheet_id,
  });
};

export const resolveLinehaulTariffFromServiceLevel = (serviceLevelLegLos, losOptions = []) => {
  if (!Array.isArray(serviceLevelLegLos) || serviceLevelLegLos.length === 0) return null;
  const lhLeg = serviceLevelLegLos.find(
    (entry) => String(entry?.location_role || "").toUpperCase() === "LINEHAUL"
  );
  const losCode = lhLeg?.los_code;
  if (!losCode) return null;
  return findLosOption(losOptions, { los_code: losCode });
};

// Mode A: true when the service level supplies a selectable linehaul tariff default.
// Pair with OLOS permission check to lock the linehaul tariff dropdown — same rule
// as pickup/delivery per-leg LOS (disableLosEdit / losEditLockedView).
export const isLinehaulTariffLockedByServiceLevel = (serviceLevelLegLos, losOptions = []) =>
  !!resolveLinehaulTariffFromServiceLevel(serviceLevelLegLos, losOptions);

export const resolveLinehaulTariffFromAccountConfig = (losOptions = []) =>
  (losOptions || []).find((opt) => opt.linehaul_config_los) || null;

export const isTruthySpecialFlag = (value) => (
  value === true || value === 'true' || value === 1 || value === '1'
);

export const resolveLinehaulSpecialFlag = ({
  tracking = {},
  order = {},
  savedSpecial,
} = {}) => {
  if (savedSpecial != null) return Boolean(savedSpecial);

  const candidates = [
    tracking?.linehaul_special,
    tracking?.lh_special,
    order?.linehaul_special,
    order?.customer_order_details?.linehaul_special,
    order?.is_linehaul_special,
    order?.customer_order_details?.is_linehaul_special,
    Array.isArray(order?.tags) && order.tags.includes('linehaul_special'),
    Array.isArray(order?.customer_order_details?.tags)
      && order.customer_order_details.tags.includes('linehaul_special'),
  ];

  return candidates.some(isTruthySpecialFlag);
};

export const normalizeLosId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    const nested = value.$oid || value.oid || value.id;
    return nested != null && nested !== "" ? String(nested) : null;
  }
  return String(value);
};

export const extractLinehaulTariffLosId = (source = {}) => normalizeLosId(
  source?.linehaul_tariff_id
  ?? source?.customer_order_details?.linehaul_tariff_id
);

export const resolveLinehaulTariffIdFromLosId = (losId, losOptions = []) => {
  const losIdStr = normalizeLosId(losId);
  if (!losIdStr || !Array.isArray(losOptions) || losOptions.length === 0) return undefined;
  const match = findLosOption(losOptions, { los_id: losIdStr });
  if (match?.id) return String(match.id);
  const directMatch = losOptions.find(
    (o) => String(o.id) === losIdStr || String(o.los_id || "") === losIdStr
  );
  return directMatch?.id ? String(directMatch.id) : undefined;
};

// Map a saved order/PUT tariff to the Select option id. Prefers the exact dropdown
// value from save, then LOS id, then code+sheet — and avoids stale tracking fallback
// when the order already has a saved linehaul tariff.
export const resolveTariffDropdownId = ({
  losOptions = [],
  savedDropdownId,
  savedLosId,
  savedTariff = {},
  order = {},
  tracking = {},
  serviceLevelLegLos,
  initialCode,
} = {}) => {
  if (!Array.isArray(losOptions) || losOptions.length === 0) return undefined;

  const normalizedDropdownId = normalizeLosId(savedDropdownId);
  if (normalizedDropdownId && losOptions.some((o) => String(o.id) === normalizedDropdownId)) {
    return normalizedDropdownId;
  }

  const orderLosId = normalizeLosId(savedLosId) ?? extractLinehaulTariffLosId(order);
  const hasSavedTariff = Boolean(orderLosId || normalizedDropdownId);

  if (orderLosId) {
    const fromLos = resolveLinehaulTariffIdFromLosId(orderLosId, losOptions);
    if (fromLos) return fromLos;
  }

  const tariffHint = savedTariff?.los_code ? savedTariff : (tracking?.tariff || {});
  if (tariffHint?.los_code || tariffHint?.los_id) {
    const match = findLosOption(losOptions, {
      los_id: normalizeLosId(tariffHint.los_id),
      los_code: tariffHint.los_code,
      rate_sheet_id: tariffHint.rate_sheet_id,
    });
    if (match?.id) return String(match.id);
  }

  if (hasSavedTariff) return undefined;

  return resolveLinehaulTariffId({
    tracking,
    serviceLevelLegLos,
    losOptions,
    initialCode,
  });
};

export const resolveLinehaulTariffSelection = ({
  tracking = {},
  serviceLevelLegLos,
  losOptions = [],
} = {}) => (
  resolveLinehaulTariffFromTracking(tracking, losOptions)
  || resolveLinehaulTariffFromServiceLevel(serviceLevelLegLos, losOptions)
  || resolveLinehaulTariffFromAccountConfig(losOptions)
);

// Definitive id for the linehaul tariff dropdown — undefined when nothing matches
// (e.g. account switch cleared the prior account's los id from the new option list).
export const resolveLinehaulTariffId = ({
  tracking = {},
  serviceLevelLegLos,
  losOptions = [],
  initialCode,
  linehaulTariffId,
} = {}) => {
  if (!Array.isArray(losOptions) || losOptions.length === 0) return undefined;

  const normalizedLinehaulTariffId = normalizeLosId(linehaulTariffId);
  const hasExplicitTariffId = normalizedLinehaulTariffId != null;
  const savedTariffLosId = hasExplicitTariffId
    ? normalizedLinehaulTariffId
    : extractLinehaulTariffLosId(tracking);
  if (savedTariffLosId) {
    const fromSavedId = resolveLinehaulTariffIdFromLosId(savedTariffLosId, losOptions);
    if (fromSavedId) return fromSavedId;
    if (hasExplicitTariffId) return undefined;
  }

  const resolved = resolveLinehaulTariffSelection({
    tracking,
    serviceLevelLegLos,
    losOptions,
  });
  if (resolved?.id) return String(resolved.id);
  const code = typeof initialCode === "string" ? initialCode : initialCode?.code;
  if (code) {
    const byCode = losOptions.find((opt) => opt.code === code);
    if (byCode?.id) return String(byCode.id);
  }
  return undefined;
};

export const MODE_B_LINEHAUL_TARIFF_NONE_ID = "NONE";

export const buildModeBLinehaulNoneOption = () => ({
  id: MODE_B_LINEHAUL_TARIFF_NONE_ID,
  code: "NONE",
  name: "None",
  rate_sheet_id: "",
  los_group: "",
});

export const parseBillingConfigurations = (configurations = []) => {
  const config = {};
  (Array.isArray(configurations) ? configurations : [])
    .filter((setting) => setting?.config_type === "BILLING")
    .forEach((setting) => {
      config[setting.setting_name] = setting.setting_value;
    });
  return config;
};

// Mode B only: derive the default linehaul tariff id from account billing + org rules.
export const resolveModeBLinehaulTariffLosId = ({
  billLinehaulWith,
  linehaulLosId,
  organizationLinhaulLosId,
} = {}) => {
  const billWith = billLinehaulWith === undefined || billLinehaulWith === ""
    ? null
    : billLinehaulWith;

  if (billWith === "accessorial" || billWith === null) {
    return MODE_B_LINEHAUL_TARIFF_NONE_ID;
  }

  if (billWith === "los") {
    const accountLosId = normalizeLosId(linehaulLosId);
    if (accountLosId) return accountLosId;
    const orgLosId = normalizeLosId(organizationLinhaulLosId);
    if (orgLosId) return orgLosId;
    return MODE_B_LINEHAUL_TARIFF_NONE_ID;
  }

  return MODE_B_LINEHAUL_TARIFF_NONE_ID;
};

export const losRecordToTariffOption = (los) => {
  if (!los) return null;
  const id = normalizeLosId(los.id || los._id || los.level_of_service_id);
  if (!id) return null;
  return {
    id: String(id),
    code: String(los.code || ""),
    name: String(los.name || los.code || id),
    rate_sheet_id: los.rate_sheet_id ? String(los.rate_sheet_id) : "",
    los_group: "",
  };
};

export const injectLosOptionIfMissing = (options = [], option) => {
  if (!option?.id) return options;
  const base = Array.isArray(options) ? options : [];
  if (base.some((o) => String(o.id) === String(option.id))) return base;
  return [...base, option];
};

export const buildModeBLinehaulTariffOptions = (baseOptions = []) => {
  const normalized = normalizeLosOptionsList(baseOptions);
  if (normalized.some((o) => String(o.id) === MODE_B_LINEHAUL_TARIFF_NONE_ID)) {
    return normalized;
  }
  return [buildModeBLinehaulNoneOption(), ...normalized];
};

export const buildLinehaulTariffPayload = (selectedOption) => {
  if (!selectedOption) return null;
  if (String(selectedOption.id) === MODE_B_LINEHAUL_TARIFF_NONE_ID) {
    return {
      los_id: "NONE",
      los_name: "None",
      los_code: "NONE",
      rate_sheet_id: "",
    };
  }
  return {
    los_id: selectedOption.id || null,
    los_name: selectedOption.name || "",
    los_code: selectedOption.code || "",
    rate_sheet_id: selectedOption.rate_sheet_id || "",
  };
};
